import {
  BookedLegDocumentUpdateInt,
  PassengerUpdateDocumentInt,
} from '@app/interfaces/requiredDocuments.interface';
import { PNRLookupAWSInt } from '@app/interfaces/reservation-aws.interface';
import { ReqBoardingPass } from '@app/models/micro/request/reqBoardingPass.model';
import { RequestContactability } from '@app/models/micro/request/requestContactability.model';
import { RequestMicroModel } from '@app/models/micro/request/requestMicro.model';
import { RequestPassengerListStandby } from '@app/models/micro/request/requestPassengerListStandby.model';
import { RequestRequireDocumentsModel } from '@app/models/micro/request/requestRequireDocumentsModel.model';
import { Reservation } from '@app/models/micro/reservation.model';
import { RequestUpdateDocuments } from '@app/models/micro/updateDocuments.model';
import { RequestPecTab } from '@app/models/micro/request/requestPecTab.model';
import { PassengerIdMapService } from '@app/services/passenger-id-map.service';
import { RequestFlightDetailsModel } from '@models/micro/request/requestFlightDetailsModel.model';
import { RespBoardingPass } from '@app/models/micro/response/respBoardingPass.model';
import { RespDoCheckIn } from '@app/models/micro/response/respDoCheckIn.model';
import { RequestEligibilityIrops } from '@app/models/micro/request/requestEligibilityIrops.model';
import { RequestSearchFlightIrops } from '@app/models/micro/request/requestSearchFlightIrops.model';
import { FlightSummaryIrops } from '@app/models/micro/flight-summary-irops.model';
import { hasUkUptWarning } from '@app/utils/functions/general';
import moment from 'moment';
import { CabinInfo, CabinResponse, CabinRquest } from '@app/interfaces/micro/cabin.interface';
import { RequestPassengerListUpgrade } from '@app/models/micro/request/requestPassengerListUpgrade.model';
export class ApiMapper {
  constructor() {}

  static requestEligibility(reservation: PNRLookupAWSInt, lookup: Reservation) {
    const requestMicroModel = new RequestMicroModel(reservation, lookup);
    return requestMicroModel;
  }

  static requestRequireDocuments(reservation: Reservation) {
    const requestRequireDocumentsModel = new RequestRequireDocumentsModel(reservation);
    return requestRequireDocumentsModel;
  }

  static requestFlightDetails(reservation: Reservation) {
    const requestFlightDetailsModel = new RequestFlightDetailsModel(reservation);
    return requestFlightDetailsModel;
  }

  static requestListstandby(reservation: Reservation) {
    const request = new RequestPassengerListStandby(reservation);
    return request;
  }

  static mapPassengerListUpgrade(reservation: Reservation) {
    return new RequestPassengerListUpgrade(reservation);
  }

  static requestBoardingPass(reservation: Reservation) {
    const requestBoardingPass = new ReqBoardingPass(reservation);
    return requestBoardingPass;
  }

  static respBoardingPass(resp: any) {
    // Validar que el backend no haya respondido con un error de negocio
    if (resp?.status === 'BusinessLogicError' || resp?.errorMessage) {
      throw {
        status: resp.status,
        code: resp.code,
        errorMessage: resp.errorMessage,
        service: resp.service,
      };
    }
    let respBoardingPass = new RespBoardingPass(resp);
    return respBoardingPass;
  }

  static requestUpdateDocuments(
    bk: BookedLegDocumentUpdateInt[],
    passenger: PassengerUpdateDocumentInt[],
    reservation: Reservation,
    tripType: string
  ) {
    const request = new RequestUpdateDocuments(bk, passenger, reservation, tripType);
    return request;
  }

  static mapContactabilityDigital(reservation: Reservation) {
    const passengers = reservation.passengers.filter(
      (passenger) => passenger.checkinSelected && passenger.sendContactability
    );
    const listPassengers = passengers.map((passenger) => {
      return new RequestContactability(passenger, reservation.pnr);
    });
    return listPassengers;
  }

  static requestPecTab(
    reservation: Reservation,
    reprintBp: boolean,
    passengerIdMapService?: PassengerIdMapService
  ) {
    const requestMicroModel = new RequestPecTab(reservation, reprintBp, passengerIdMapService);
    return requestMicroModel;
  }

  static respDoCheckIn(resp: any) {
    // CI-23791: con el warning UK-UPT nadie queda documentado (checkinStatus
    // false para todos) y validateCheckin fallaria — pero ese caso SI debe
    // mapearse (no truena) para que el warning llegue intacto a
    // ImportantInfoComponent, que ya sabe mostrar el mensaje de mostrador en
    // vez del error generico.
    if (this.validateCheckin(resp) || hasUkUptWarning(resp?.warnings)) {
      return new RespDoCheckIn(resp);
    } else {
      throw {
        message: 'error',
      };
    }
  }

  static validateCheckin(response) {
    return response.passengers.every((p) => {
      const [element] = p?.checkinStatusBySegment;
      return element?.checkinStatus || element?.onStandByList;
    });
  }

  //#region Irops
  static mapEligibilityIrops(reservation: Reservation) {
    const requestMicroModel = new RequestEligibilityIrops(reservation);
    return requestMicroModel;
  }
  //#endregion Irops

  static getResquestAssingSeat(reservation: Reservation, newFlight?: FlightSummaryIrops) {
    const newFlightDepartureDate =
      newFlight?.departureDateTime ?? newFlight?.segments?.[0]?.departureDateTime;

    const seatInfo = reservation.flightSummary.segments
      .filter((segment) => segment.isCancelOag || segment.isChangeOag)
      .map((segment, index) => {
        if (!segment?.flightsNewInfo) return null;

        const seats = reservation.passengers.map((passenger) => {
          const seat = passenger.seats?.find((s) => s.segmentCode === segment.segmentCode);
          return seat?.seatCode ? { seatCode: seat.seatCode } : null;
        });

        const segmentNewFlight = newFlight?.segments?.[index];
        const departureDate =
          segmentNewFlight?.departureDateTime ??
          newFlightDepartureDate ??
          segment.flightsNewInfo.departureDateTime;

        return {
          newSegment: {
            departure: segment.departureAirport,
            arrival: segment.arrivalAirport,
            departureDate,
          },
          seats,
        };
      });

    return {
      seatInfo,
    };
  }

  static mapSearchFlightIrops(
    reservation: Reservation,
    departureDate: string,
    cabin?: CabinInfo[]
  ) {
    const requestMicroModel = new RequestSearchFlightIrops(reservation, departureDate, cabin);
    return requestMicroModel;
  }
  //#endregion Irops

  static requestCabin(reservation: PNRLookupAWSInt): CabinRquest {
    return {
      legCollection: reservation.bookedLegCollection.map((legElement) => {
        const { legCode, legRegion: region, segments } = legElement;
        const [firstSegment, ...nextSegments] = segments;
        const leg = legCode.substring(0, 7);
        const lastSegment =
          nextSegments && nextSegments.length > 0
            ? nextSegments[nextSegments.length - 1]
            : firstSegment;

        return {
          leg,
          region,
          subRegionDestination: lastSegment.arrivalAirportRegion,
          subRegionOrigin: firstSegment.departureAirportRegion,
          departureDate: moment(firstSegment.departureDateTime).format('YYYY-MM-DD'),
          departureAirportCountry: firstSegment.departureCountry,
          arrivalAirportCountry: lastSegment.arrivalCountry,
        };
      }),
    };
  }
  static respCabin(response: CabinResponse) {
    return response.cabins;
  }
}
